import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, LinkLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {snackbarAtom} from 'atoms/snackbar';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';

import styles from './EnhancedHeadingNode.module.css';
import {setPersistedHeadingCollapsed} from './HeadingCollapsePersistence';
import {findHeadingNodeByHeadingId} from './HeadingNodeUtils';

interface HeadingControlsProps {
  headingId: string;
}

export const HeadingControls = ({headingId}: HeadingControlsProps) => {
  const [editor] = useLexicalComposerContext();
  const collabId = useCollabId();
  const setSnackbar = useSetAtom(snackbarAtom);

  // Get initial collapsed state immediately
  const getInitialCollapsedState = () => {
    let initialState = false;
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);
      if (node) {
        initialState = node.getCollapsed();
      }
    });
    return initialState;
  };

  const [collapsed, setCollapsed] = useState(getInitialCollapsedState);
  const prevCollapsedRef = useRef(collapsed);

  // Sync with node's collapsed state
  useEffect(() => {
    const updateCollapsedState = () => {
      editor.getEditorState().read(() => {
        const node = findHeadingNodeByHeadingId(editor, headingId);
        if (node) {
          const newCollapsed = node.getCollapsed();
          // Only update if the state actually changed
          if (prevCollapsedRef.current !== newCollapsed) {
            prevCollapsedRef.current = newCollapsed;
            setCollapsed(newCollapsed);
          }
        }
      });
    };

    // Initial update
    updateCollapsedState();

    // Listen for editor updates
    const removeUpdateListener = editor.registerUpdateListener(() => {
      updateCollapsedState();
    });

    return removeUpdateListener;
  }, [editor, headingId]);

  const handleToggleCollapse = useCallback(
    (event: React.MouseEvent) => {
      // Prevent the click from inserting a cursor in the editor
      event.preventDefault();
      event.stopPropagation();

      editor.update(() => {
        const node = findHeadingNodeByHeadingId(editor, headingId);
        if (node) {
          node.toggleCollapsed();
          const isNowCollapsed = node.getCollapsed();
          setPersistedHeadingCollapsed(collabId, headingId, isNowCollapsed);

          // Force the node to be marked as dirty so transforms and Yjs property sync run.
          node.markDirty();

          prevCollapsedRef.current = isNowCollapsed;
          setCollapsed(isNowCollapsed);
        }
      });
    },
    [collabId, editor, headingId]
  );

  const handleCopyLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      try {
        const url = new URL(window.location.href);
        // Remove "heading-" prefix for shorter URLs
        const shortId = headingId.replace(/^heading-/, '');
        url.hash = `hid=${shortId}`;
        navigator.clipboard.writeText(url.toString());
        setSnackbar({text: t('copied')});
      } catch {
        setSnackbar({text: t('error_copying_link')});
      }
    },
    [headingId, setSnackbar]
  );

  return (
    <Box display="flex" alignItems="center" style={{gap: 2}}>
      <Tooltip title={t('copy_link')}>
        <IconButton
          variant="borderless"
          size="small"
          onClick={handleCopyLink}
          className={styles.linkIcon}
          style={{
            userSelect: 'none',
          }}
          aria-label="icon button"
        >
          <UIIcon src={LinkLine} size="small" color="subtle" />
        </IconButton>
      </Tooltip>
      <IconButton
        variant="borderless"
        size="small"
        onClick={handleToggleCollapse}
        style={{
          userSelect: 'none',
        }}
        aria-label="icon button"
      >
        <UIIcon src={collapsed ? ChevronRightLine : ChevronDownLine} size="small" color="subtle" />
      </IconButton>
    </Box>
  );
};
